// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { managerTeamKRAsUrl, submitManagerReviewUrl, saveManagerReviewDraftUrl } from "../../../Service/Apis";
import {
  getStatusChip,
  formatDate,
  calculateWeightedRating,
  KRAStatus,
  getPeriodShortLabel,
  getPeriodDaysWarningColor,
  formatRatingLabel,
} from "../utils";
import RatingInput from "./components/RatingInput";

interface KPIRating {
  kpi_id: string;
  question: string;
  weightage: number;
  rating_type?: 'numerical' | 'likert' | 'boolean';
  self_rating: number | null;
  manager_rating: number | null;
  self_comments?: string | null;
  manager_comments?: string | null;
}

interface KRASectionRating {
  section_id: string;
  section: string;
  weightage: number;
  questions: KPIRating[];
}

interface EmployeeKRA {
  employee_kra_id: string;
  employee_name: string;
  employee_code: string;
  employee_email: string;
  form_name: string;
  designation: string;
  assigned_date: string;
  accepted_date?: string | null;
  self_review_submitted_date?: string | null;
  manager_review_submitted_date?: string | null;
  status: KRAStatus;
  sections: KRASectionRating[];
  manager_overall_rating: number | null;
  self_review_overall_rating: number | null;
  self_review_overall_comments?: string;
  manager_overall_comments?: string;
  review_period: string;
  period_start_date: string;
  period_end_date: string;
  days_remaining: number;
  is_period_expired: boolean;
}

const ManagerKRAReview: React.FC = () => {
  // useState declarations
  const [teamKRAs, setTeamKRAs] = useState<EmployeeKRA[]>([]);
  const [filteredKRAs, setFilteredKRAs] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeKRA | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showCommentsError, setShowCommentsError] = useState<boolean>(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Other constants and hooks
  const showToast = useCustomToast();

  useEffect(() => {
    applyFiltersAndSort();
  }, [teamKRAs, statusFilter, searchQuery, sortField, sortOrder]);
  // Redux variables
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const fetchTeamKRAs = async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData(managerTeamKRAsUrl));
      
      if (response && Array.isArray(response)) {
        setTeamKRAs(response);
      } else {
        setTeamKRAs([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load team KRAs";
      showToast(errorMessage, "error", true);
      setTeamKRAs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...teamKRAs];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((kra) => kra.status === statusFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (kra) =>
          kra.employee_name.toLowerCase().includes(query) ||
          kra.employee_code.toLowerCase().includes(query) ||
          kra.employee_email.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    if (sortField) {
      filtered = filtered.sort((a, b) => {
        let aValue: string | number | undefined;
        let bValue: string | number | undefined;
        
        switch (sortField) {
          case "employee_name":
            aValue = a.employee_name?.toLowerCase() || "";
            bValue = b.employee_name?.toLowerCase() || "";
            break;
          case "form_name":
            aValue = a.form_name?.toLowerCase() || "";
            bValue = b.form_name?.toLowerCase() || "";
            break;
          case "review_period":
            // Map period to sortable values
            const periodOrder: { [key: string]: number } = {
              'monthly': 1,
              'quarterly': 2,
              'half_yearly': 3,
              'yearly': 4
            };
            aValue = periodOrder[a.review_period] || 0;
            bValue = periodOrder[b.review_period] || 0;
            break;
          case "self_review_overall_rating":
            aValue = a.self_review_overall_rating ?? -1;
            bValue = b.self_review_overall_rating ?? -1;
            break;
          case "manager_overall_rating":
            aValue = a.manager_overall_rating ?? -1;
            bValue = b.manager_overall_rating ?? -1;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredKRAs(filtered);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleReviewClick = (employee: EmployeeKRA) => {
    // Deep copy to avoid state mutation
    setSelectedEmployee(JSON.parse(JSON.stringify(employee)));
    setReviewDialogOpen(true);
  };

  const handleRatingChange = (
    sectionIndex: number,
    questionIndex: number,
    rating: number
  ) => {
    if (!selectedEmployee) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedEmployee)) as EmployeeKRA;
    updated.sections[sectionIndex].questions[questionIndex].manager_rating = rating;
    setSelectedEmployee(updated);
  };

  const handleKPICommentChange = (
    sectionIndex: number,
    questionIndex: number,
    comment: string
  ) => {
    if (!selectedEmployee) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedEmployee)) as EmployeeKRA;
    updated.sections[sectionIndex].questions[questionIndex].manager_comments = comment;
    setSelectedEmployee(updated);
  };

  const handleOverallCommentsChange = (comments: string) => {
    if (!selectedEmployee) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedEmployee)) as EmployeeKRA;
    updated.manager_overall_comments = comments;
    setSelectedEmployee(updated);
    // Clear error when user starts typing
    if (showCommentsError && comments.trim() !== "") {
      setShowCommentsError(false);
    }
  };

  const calculateAverageRating = () => {
    if (!selectedEmployee) return 0;
    return calculateWeightedRating(selectedEmployee.sections, 'manager_rating');
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!selectedEmployee) return;

    if (savingDraft) return; // Prevent double save

    setSavingDraft(true);
    try {
      const sectionsData = selectedEmployee.sections.map((section) => ({
        section_id: section.section_id,
        questions: section.questions.map((q) => ({
          kpi_id: q.kpi_id,
          manager_rating: q.manager_rating,
          manager_comments: q.manager_comments || "",
        })),
      }));

      const payload = {
        sections: sectionsData,
        manager_overall_rating: calculateAverageRating(),
        manager_overall_comments: selectedEmployee.manager_overall_comments || "",
      };

      await axios.post(
        saveManagerReviewDraftUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      showToast("Draft saved successfully! Refreshing data...", "success", true);
      
      // Refresh the data to show saved draft
      await fetchTeamKRAs();
      
      // Close dialog to show updated data
      setReviewDialogOpen(false);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || "Failed to save draft";
      showToast(errorMessage, "error", true);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedEmployee) return;

    // Prevent double submission
    if (submitting) return;

    // Validation: Check if all KPIs are rated (null/undefined check only, 0 is valid)
    let allRated = true;
    let unratedKPIs: string[] = [];
    
    selectedEmployee.sections.forEach((section) => {
      section.questions.forEach((kpi) => {
        if (kpi.manager_rating === null || kpi.manager_rating === undefined) {
          allRated = false;
          unratedKPIs.push(kpi.question.substring(0, 50) + "...");
        }
      });
    });

    if (!allRated) {
      showToast(
        `Please rate all KPIs before submitting. Missing ${unratedKPIs.length} rating(s).`,
        "error",
        true
      );
      return;
    }

    // Validation: Check if overall comments are provided
    if (!selectedEmployee.manager_overall_comments || selectedEmployee.manager_overall_comments.trim() === "") {
      setShowCommentsError(true);
      showToast(
        "Please provide overall comments before submitting your review.",
        "error",
        true
      );
      return;
    }
    setShowCommentsError(false);

    // Auto-calculate overall rating
    const overallRating = calculateAverageRating();
    
    // Round to 2 decimal places to match backend DecimalField(max_digits=3, decimal_places=2)
    const roundedRating = Math.round(overallRating * 100) / 100;

    setSubmitting(true);
    try {
      const sectionsData = selectedEmployee.sections.map((section) => ({
        section_id: section.section_id,
        questions: section.questions.map((kpi) => ({
          kpi_id: kpi.kpi_id,
          manager_rating: kpi.manager_rating || 0,
          manager_comments: kpi.manager_comments || "",
        })),
      }));

      const payload = {
        sections: sectionsData,
        manager_overall_rating: roundedRating,
        manager_overall_comments: selectedEmployee.manager_overall_comments || "",
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        submitManagerReviewUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.success) {
        // Update local state with response data
        setTeamKRAs((prev) =>
          prev.map((kra) =>
            kra.employee_kra_id === selectedEmployee.employee_kra_id
              ? response.data.data
              : kra
          )
        );

        showToast("Review submitted successfully!", "success", true);
        setReviewDialogOpen(false);
        setSelectedEmployee(null);
      } else {
        throw new Error(response.data?.message || "Failed to submit review");
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to submit review. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmitting(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchTeamKRAs();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [teamKRAs, statusFilter, searchQuery, sortField, sortOrder]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
        >
          Team KRA Review
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and rate your team members' Key Responsibility Areas and Key Performance Indicators
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, code, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending Acceptance</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="awaiting_manager_review">Awaiting Manager Review</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Empty State */}
      {filteredKRAs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <PersonIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Team Members Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {teamKRAs.length === 0
              ? "You don't have any team members with assigned KRAs yet."
              : "No team members match your search criteria."}
          </Typography>
        </Paper>
      ) : (
        /* Team Members Table */
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "employee_name"}
                    direction={sortField === "employee_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("employee_name")}
                  >
                    Employee
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "form_name"}
                    direction={sortField === "form_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("form_name")}
                  >
                    KRA
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Assigned</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "review_period"}
                    direction={sortField === "review_period" ? sortOrder : "asc"}
                    onClick={() => handleSort("review_period")}
                  >
                    Period
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "self_review_overall_rating"}
                    direction={sortField === "self_review_overall_rating" ? sortOrder : "asc"}
                    onClick={() => handleSort("self_review_overall_rating")}
                  >
                    Self Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "manager_overall_rating"}
                    direction={sortField === "manager_overall_rating" ? sortOrder : "asc"}
                    onClick={() => handleSort("manager_overall_rating")}
                  >
                    Manager Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredKRAs.map((employee) => (
                <TableRow key={employee.employee_kra_id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "600" }}>
                        {employee.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.employee_code} • {employee.employee_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{employee.form_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(employee.assigned_date)}
                    </Typography>
                    {employee.accepted_date && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Accepted: {formatDate(employee.accepted_date)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Chip 
                        label={getPeriodShortLabel(employee.review_period)} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        End: {formatDate(employee.period_end_date)}
                      </Typography>
                      {employee.days_remaining > 0 && !employee.is_period_expired && (
                        <Chip 
                          label={`${employee.days_remaining}d left`}
                          size="small"
                          color={getPeriodDaysWarningColor(employee.days_remaining)}
                        />
                      )}
                      {employee.is_period_expired && (
                        <Chip label="Expired" size="small" color="error" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(employee.status)}</TableCell>
                  
                  {/* Self Score */}
                  <TableCell>
                    {employee.self_review_overall_rating != null ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "600", color: "info.main", mb: 0.5 }}>
                          {Number(employee.self_review_overall_rating).toFixed(2)}/5.00
                        </Typography>
                        <Typography variant="caption" sx={{ color: "info.main", fontWeight: "600" }}>
                          {((Number(employee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not rated
                      </Typography>
                    )}
                  </TableCell>
                  
                  {/* Manager Score */}
                  <TableCell>
                    {employee.manager_overall_rating != null ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "600", color: "primary.main", mb: 0.5 }}>
                          {Number(employee.manager_overall_rating).toFixed(2)}/5.00
                        </Typography>
                        <Typography variant="caption" sx={{ color: "primary.main", fontWeight: "600" }}>
                          {((Number(employee.manager_overall_rating) / 5) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not rated
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell align="right">
                    <Button
                      variant={employee.status === "awaiting_manager_review" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleReviewClick(employee)}
                      disabled={employee.status === "pending" || employee.status === "accepted"}
                    >
                      {employee.status === "pending"
                        ? "Pending"
                        : employee.status === "accepted"
                        ? "Awaiting Self-Review"
                        : employee.manager_overall_rating
                        ? "View Review"
                        : "Review"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => !submitting && !savingDraft && setReviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Review KRA - {selectedEmployee?.employee_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedEmployee?.form_name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEmployee && (
            <Box>
              {/* KRA Sections */}
              {selectedEmployee.sections.map((section, sectionIndex) => (
                <Accordion key={sectionIndex} defaultExpanded elevation={0} sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: "grey.50" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: "600" }}>
                        {sectionIndex + 1}. {section.section}
                      </Typography>
                      <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                        {section.weightage}%
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {section.questions.map((kpi, questionIndex) => (
                        <Box key={questionIndex}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                              {kpi.question}
                            </Typography>
                            <Chip
                              label={`${kpi.weightage}%`}
                              size="small"
                              color="primary"
                              sx={{ ml: 2 }}
                            />
                          </Box>
                          
                          {/* Employee Self-Rating and Manager Rating Side-by-Side */}
                          {kpi.self_rating != null && typeof kpi.self_rating === 'number' ? (
                            <Box sx={{ mt: 2, p: 2.5, backgroundColor: "#f1f8f4", borderRadius: 1, border: "2px solid #81c784" }}>
                              <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
                                {/* Employee Self-Rating */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: "600", display: "block", mb: 1.5, color: "#000" }}>
                                    Employee Self-Rating:
                                  </Typography>
                                  <Box>
                                    <Typography variant="h5" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                                      {formatRatingLabel(kpi.self_rating, kpi.rating_type)}{kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                                      {((kpi.self_rating / 5) * 100).toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Manager Rating */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: "600", display: "block", mb: 1.5, color: "#000" }}>
                                    Manager Rating:
                                  </Typography>
                                  <Box sx={{ mb: 2 }}>
                                    <RatingInput
                                      ratingType={kpi.rating_type}
                                      value={kpi.manager_rating}
                                      onChange={(value) => handleRatingChange(sectionIndex, questionIndex, value)}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography variant="h5" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                                      {kpi.manager_rating !== null && kpi.manager_rating !== undefined ? `${formatRatingLabel(kpi.manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : 'Not Rated'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "success.main", fontWeight: "600" }}>
                                      {kpi.manager_rating && kpi.manager_rating > 0 ? `${((kpi.manager_rating / 5) * 100).toFixed(1)}%` : '0.0%'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                              
                              {/* Employee Self-Comments (Read-only) */}
                              {kpi.self_comments && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 1 }}>
                                    Employee's Comments:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                                    {kpi.self_comments}
                                  </Typography>
                                </Box>
                              )}

                              {/* Manager Comment for this KPI */}
                              <Box sx={{ mt: 2 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={3}
                                  label="Your Comments for this KPI (Optional)"
                                  placeholder="Provide specific feedback on this KPI performance..."
                                  value={kpi.manager_comments || ""}
                                  onChange={(e) => handleKPICommentChange(sectionIndex, questionIndex, e.target.value)}
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#f1f8f4',
                                    },
                                  }}
                                />
                              </Box>
                              
                            </Box>
                          ) : (
                            // If no self-rating, show manager rating separately
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, display: "block", fontWeight: "600" }}>
                                Manager Rating:
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <RatingInput
                                  ratingType={kpi.rating_type}
                                  value={kpi.manager_rating}
                                  onChange={(value) => handleRatingChange(sectionIndex, questionIndex, value)}
                                />
                              </Box>
                              <Box>
                                <Typography variant="h5" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                                  {kpi.manager_rating !== null && kpi.manager_rating !== undefined ? `${formatRatingLabel(kpi.manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : 'Not Rated'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "success.main", fontWeight: "600" }}>
                                  {kpi.manager_rating && kpi.manager_rating > 0 ? `${((kpi.manager_rating / 5) * 100).toFixed(1)}%` : '0.0%'}
                                </Typography>
                              </Box>
                              
                              {/* Employee Self-Comments (Read-only) */}
                              {kpi.self_comments && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 1 }}>
                                    Employee's Comments:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                                    {kpi.self_comments}
                                  </Typography>
                                </Box>
                              )}

                              {/* Manager Comment for this KPI */}
                              <Box sx={{ mt: 2 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={3}
                                  label="Your Comments for this KPI (Optional)"
                                  placeholder="Provide specific feedback on this KPI performance..."
                                  value={kpi.manager_comments || ""}
                                  onChange={(e) => handleKPICommentChange(sectionIndex, questionIndex, e.target.value)}
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#f1f8f4',
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          {questionIndex < section.questions.length - 1 && (
                            <Divider sx={{ mt: 3 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Divider sx={{ my: 3 }} />

              {/* Display Employee's Overall Comments if provided */}
              {selectedEmployee.self_review_overall_comments && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                    💭 Employee's Overall Comments
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
                      {selectedEmployee.self_review_overall_comments}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Manager Overall Feedback */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                  Manager Overall Feedback
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  required
                  label="Your Overall Comments & Feedback *"
                  placeholder="Provide comprehensive feedback on the employee's overall performance, strengths, areas for improvement, and recommendations..."
                  value={selectedEmployee.manager_overall_comments || ""}
                  onChange={(e) => handleOverallCommentsChange(e.target.value)}
                  variant="outlined"
                  error={showCommentsError && (!selectedEmployee.manager_overall_comments || selectedEmployee.manager_overall_comments.trim() === "")}
                  helperText={showCommentsError && (!selectedEmployee.manager_overall_comments || selectedEmployee.manager_overall_comments.trim() === "") ? "Overall comments are required" : ""}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Employee and Manager Ratings Side by Side */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Employee Self-Rating */}
                {selectedEmployee.self_review_overall_rating != null && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Employee Self-Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                        {Number(selectedEmployee.self_review_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Manager Rating */}
                <Grid item xs={12} md={selectedEmployee.self_review_overall_rating != null ? 6 : 12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                    Manager Rating
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                      {Number(calculateAverageRating()).toFixed(2)}/5.00
                    </Typography>
                    <Typography variant="h6" sx={{ color: "success.main", fontWeight: "600" }}>
                      {((Number(calculateAverageRating()) / 5) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={submitting || savingDraft}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={submitting || savingDraft}
            startIcon={savingDraft ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {savingDraft ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={submitting || savingDraft}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManagerKRAReview;
